import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ArrowDropDownRounded,
  AutoStoriesRounded,
  ContentCopyRounded,
  DeleteOutlineRounded,
  DescriptionRounded,
  EditNoteRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  SearchRounded,
  UploadFileRounded,
  WarningAmberRounded,
  WorkspacePremiumRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, query, type DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { courseTemplateApi } from '../../lib/api/course-template.api';
import { CourseTemplateEditor } from './components/CourseTemplateEditor';
import { SyllabusProposalsInbox } from './components/SyllabusProposalsInbox';
import { EmptyTemplatesIllustration } from './components/EmptyTemplatesIllustration';
import { errorText } from '../../lib/error-text';
import { formatDate } from '../../lib/i18n-format';

// ─── Types ──────────────────────────────────────────────────────────────────

type TemplateStatus = 'draft' | 'published' | 'archived';
type TemplateSource = 'manual' | 'pdf-import' | 'duplicated';

interface TemplateRow {
  id: string;
  title: string;
  code?: string;
  version: number;
  status: TemplateStatus;
  source: TemplateSource;
  importedFrom?: string;
  programId?: string;
  /** The CCD is the card of the COURSE, so a template with no course has no
   *  card to open. */
  courseId?: string;
  moduleCount: number;
  assignmentCount: number;
  hasDescription: boolean;
  updatedAt?: Date;
}

interface ProgramSummary {
  id: string;
  name: string;
  code: string;
  primaryColor: string;
}

// Fallback palette indexed by program id hash when program has no primaryColor.
const FALLBACK_PALETTE = [
  '#7C3AED',
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#EC4899',
  '#6366F1',
];

function hashStringToIndex(s: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % mod;
}

function programColor(p: ProgramSummary | undefined, fallbackId: string): string {
  if (p?.primaryColor) return p.primaryColor;
  const idx = hashStringToIndex(p?.id ?? fallbackId, FALLBACK_PALETTE.length);
  return FALLBACK_PALETTE[idx] ?? FALLBACK_PALETTE[0]!;
}

function isIncomplete(t: TemplateRow): boolean {
  return t.moduleCount === 0 && t.assignmentCount === 0 && !t.hasDescription;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function CourseTemplatesPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [programs, setPrograms] = useState<ProgramSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ─── Filters ──
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TemplateStatus | 'all'>('all');
  const [programFilter, setProgramFilter] = useState<string | 'all' | 'none'>('all');
  const [sourceFilter, setSourceFilter] = useState<TemplateSource | 'all'>('all');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // ─── Programs (for grouping + color) ──
  // Filter to active only so soft-deleted programs (status: 'inactive' or
  // 'archived') stop appearing in the filter dropdown + grouping.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      const out: ProgramSummary[] = snap.docs
        .filter(
          (d) =>
            (((d.data() as DocumentData)['status'] as string | undefined) ?? 'active') === 'active',
        )
        .map((d) => {
          const data = d.data() as DocumentData;
          return {
            id: d.id,
            name: (data['name'] as string) ?? d.id,
            code: (data['code'] as string) ?? '',
            primaryColor:
              (data['primaryColor'] as string | undefined) ??
              FALLBACK_PALETTE[hashStringToIndex(d.id, FALLBACK_PALETTE.length)] ??
              FALLBACK_PALETTE[0]!,
          };
        });
      setPrograms(out);
    });
    return () => unsub();
  }, [currentTenantId]);

  // ─── Templates ──
  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, `tenants/${currentTenantId}/courseTemplates`)),
      (snap) => {
        const rows: TemplateRow[] = snap.docs
          .map((d) => {
            const data = d.data() as DocumentData;
            const description = data['description'] as { content?: unknown[] } | undefined;
            const hasDescription = Array.isArray(description?.content)
              ? description.content.some(
                  (n) =>
                    typeof n === 'object' &&
                    n !== null &&
                    Array.isArray((n as Record<string, unknown>)['content']) &&
                    ((n as { content: unknown[] }).content.length ?? 0) > 0,
                )
              : false;
            const source =
              ((data['source'] as { type?: TemplateSource } | undefined)?.type as
                | TemplateSource
                | undefined) ?? 'manual';
            const importedFrom = (data['source'] as { importedFrom?: string } | undefined)
              ?.importedFrom;
            return {
              id: d.id,
              title: data['title'] as string,
              ...(data['code'] ? { code: data['code'] as string } : {}),
              ...(data['programId'] ? { programId: data['programId'] as string } : {}),
              ...(data['courseId'] ? { courseId: data['courseId'] as string } : {}),
              version: (data['version'] as number) ?? 1,
              status: ((data['status'] as TemplateStatus) ?? 'draft') as TemplateStatus,
              source,
              ...(importedFrom !== undefined ? { importedFrom } : {}),
              moduleCount: Array.isArray(data['modules']) ? (data['modules'] as []).length : 0,
              assignmentCount: Array.isArray(data['assignments'])
                ? (data['assignments'] as []).length
                : 0,
              hasDescription,
              ...(data['updatedAt']
                ? { updatedAt: (data['updatedAt'] as { toDate(): Date }).toDate() }
                : {}),
            } satisfies TemplateRow;
          })
          .filter(
            (t) => !(snap.docs.find((d) => d.id === t.id)?.data() as DocumentData)['deletedAt'],
          );
        setTemplates(rows);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [currentTenantId]);

  // ─── Filter + Group ──
  const programById = useMemo(() => {
    const m = new Map<string, ProgramSummary>();
    for (const p of programs) m.set(p.id, p);
    return m;
  }, [programs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates
      .filter((t) => {
        if (q) {
          const hay = `${t.title} ${t.code ?? ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (statusFilter !== 'all' && t.status !== statusFilter) return false;
        if (programFilter === 'none' && t.programId) return false;
        if (programFilter !== 'all' && programFilter !== 'none' && t.programId !== programFilter)
          return false;
        if (sourceFilter !== 'all' && t.source !== sourceFilter) return false;
        return true;
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [templates, search, statusFilter, programFilter, sourceFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, TemplateRow[]>();
    for (const t of filtered) {
      const key = t.programId ?? '__none__';
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    }
    // Preserve program order then unassigned at the bottom.
    const ordered: { key: string; label: string; items: TemplateRow[]; color: string }[] = [];
    for (const p of programs) {
      const items = map.get(p.id);
      if (items && items.length > 0) {
        ordered.push({
          key: p.id,
          label: `${p.code ? `${p.code} — ` : ''}${p.name}`,
          items,
          color: programColor(p, p.id),
        });
      }
    }
    const orphan = map.get('__none__');
    if (orphan && orphan.length > 0) {
      ordered.push({
        key: '__none__',
        label: t('courseTemplates.groups.unassigned'),
        items: orphan,
        color: '#64748B',
      });
    }
    return ordered;
  }, [filtered, programs, t]);

  // ─── Stats ──
  const stats = useMemo(() => {
    return {
      total: templates.length,
      published: templates.filter((t) => t.status === 'published').length,
      draft: templates.filter((t) => t.status === 'draft').length,
      incomplete: templates.filter((t) => isIncomplete(t)).length,
    };
  }, [templates]);

  const handleDelete = async () => {
    if (!currentTenantId || !deletingId) return;
    try {
      await courseTemplateApi.deleteTemplate({
        tenantId: currentTenantId,
        templateId: deletingId,
      });
      setDeletingId(null);
    } catch (e) {
      setError(errorText(e, t('courseTemplates.errorGeneric')));
    }
  };

  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const handleDuplicate = async (sourceId: string) => {
    if (!currentTenantId) return;
    setDuplicatingId(sourceId);
    try {
      const { id } = await courseTemplateApi.duplicateTemplate({
        tenantId: currentTenantId,
        sourceTemplateId: sourceId,
      });
      setEditingId(id); // open the fresh copy in the editor
    } catch (e) {
      setError(errorText(e, t('courseTemplates.errorGeneric')));
    } finally {
      setDuplicatingId(null);
    }
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (editingId) {
    return <CourseTemplateEditor templateId={editingId} onClose={() => setEditingId(null)} />;
  }

  const filtersActive =
    !!search || statusFilter !== 'all' || programFilter !== 'all' || sourceFilter !== 'all';
  const visibleCount = filtered.length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
            {t('courseTemplates.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, mt: 0.5 }}>
            {t('courseTemplates.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => setNewOpen(true)}
          sx={{ px: 2, borderRadius: 2 }}
        >
          {t('courseTemplates.splitButton.new')}
        </Button>
      </Stack>

      {/* ── Stats row ──────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        mb={3}
        sx={{ '& > *': { flex: 1 } }}
      >
        <StatCard
          label={t('courseTemplates.stats.total')}
          value={stats.total}
          color="primary"
          icon={<AutoStoriesRounded />}
        />
        <StatCard
          label={t('courseTemplates.stats.published')}
          value={stats.published}
          color="success"
          icon={<DescriptionRounded />}
        />
        <StatCard
          label={t('courseTemplates.stats.draft')}
          value={stats.draft}
          color="warning"
          icon={<EditNoteRounded />}
        />
        <StatCard
          label={t('courseTemplates.stats.incomplete')}
          value={stats.incomplete}
          color="error"
          icon={<WarningAmberRounded />}
        />
      </Stack>

      {/* ── Search + filters ───────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            size="small"
            placeholder={t('courseTemplates.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <FilterChip
              label={t('courseTemplates.filters.status.label')}
              value={statusFilter}
              options={[
                { value: 'all', label: t('courseTemplates.filters.status.all') },
                { value: 'draft', label: t('courseTemplates.filters.status.draft') },
                { value: 'published', label: t('courseTemplates.filters.status.published') },
                { value: 'archived', label: t('courseTemplates.filters.status.archived') },
              ]}
              onChange={(v) => setStatusFilter(v as TemplateStatus | 'all')}
            />
            <FilterChip
              label={t('courseTemplates.filters.program.label')}
              value={programFilter}
              options={[
                { value: 'all', label: t('courseTemplates.filters.program.all') },
                { value: 'none', label: t('courseTemplates.filters.program.none') },
                ...programs.map((p) => ({
                  value: p.id,
                  label: p.code ? `${p.code} — ${p.name}` : p.name,
                })),
              ]}
              onChange={(v) => setProgramFilter(v as string | 'all' | 'none')}
            />
            <FilterChip
              label={t('courseTemplates.filters.source.label')}
              value={sourceFilter}
              options={[
                { value: 'all', label: t('courseTemplates.filters.source.all') },
                { value: 'manual', label: t('courseTemplates.filters.source.manual') },
                { value: 'pdf-import', label: t('courseTemplates.filters.source.pdfImport') },
                { value: 'duplicated', label: t('courseTemplates.filters.source.duplicated') },
              ]}
              onChange={(v) => setSourceFilter(v as TemplateSource | 'all')}
            />
            {filtersActive && (
              <Chip
                size="small"
                label={t('courseTemplates.resultCount', { count: visibleCount })}
                variant="outlined"
                onDelete={() => {
                  setSearch('');
                  setStatusFilter('all');
                  setProgramFilter('all');
                  setSourceFilter('all');
                }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Propuestas de los profesores (F5) ──────────────────────────── */}
      {/* Se monta sobre la lista y desaparece sola cuando no hay pendientes. */}
      <SyllabusProposalsInbox />

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Loading ────────────────────────────────────────────────────── */}
      {loading && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">{t('courseTemplates.loading')}</Typography>
        </Stack>
      )}

      {/* ── Empty state ────────────────────────────────────────────────── */}
      {!loading && templates.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3, mt: 2 }}>
          <Box sx={{ mx: 'auto', maxWidth: 480 }}>
            <EmptyTemplatesIllustration />
            <Typography variant="h5" fontWeight={700} mt={3}>
              {t('courseTemplates.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              {t('courseTemplates.empty.description')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                startIcon={<AddRounded />}
                onClick={() => setNewOpen(true)}
              >
                {t('courseTemplates.empty.create')}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* ── No results from filters ────────────────────────────────────── */}
      {!loading && templates.length > 0 && visibleCount === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">{t('courseTemplates.noResults')}</Typography>
        </Paper>
      )}

      {/* ── Grouped list ───────────────────────────────────────────────── */}
      <Stack spacing={2}>
        {grouped.map((group) => {
          const collapsed = collapsedGroups.has(group.key);
          return (
            <Box key={group.key}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  cursor: 'pointer',
                  py: 1,
                  userSelect: 'none',
                }}
                onClick={() => toggleGroup(group.key)}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 24,
                    borderRadius: 1,
                    bgcolor: group.color,
                  }}
                />
                <Typography variant="subtitle1" fontWeight={700}>
                  {group.label}
                </Typography>
                <Chip size="small" label={group.items.length} sx={{ height: 20, fontSize: 12 }} />
                <Box sx={{ flex: 1 }} />
                <IconButton size="small">
                  {collapsed ? <ExpandMoreRounded /> : <ExpandLessRounded />}
                </IconButton>
              </Stack>
              <Collapse in={!collapsed}>
                <Stack spacing={1}>
                  {group.items.map((t) => (
                    <TemplateCard
                      key={t.id}
                      template={t}
                      program={t.programId ? programById.get(t.programId) : undefined}
                      onEdit={() => setEditingId(t.id)}
                      onDelete={() => setDeletingId(t.id)}
                      onDuplicate={() => handleDuplicate(t.id)}
                      onCcd={() => navigate(`/cap/ficha/${t.courseId}`)}
                      duplicating={duplicatingId === t.id}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      <NewTemplateDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => {
          setNewOpen(false);
          setEditingId(id);
        }}
      />

      <Dialog open={!!deletingId} onClose={() => setDeletingId(null)}>
        <DialogTitle>{t('courseTemplates.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('courseTemplates.deleteDialog.body')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingId(null)}>
            {t('courseTemplates.deleteDialog.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            {t('courseTemplates.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: 'primary' | 'success' | 'warning' | 'error';
  icon: JSX.Element;
}): JSX.Element {
  const bg: Record<typeof color, string> = {
    primary: 'rgba(124,58,237,0.08)',
    success: 'rgba(16,185,129,0.08)',
    warning: 'rgba(245,158,11,0.08)',
    error: 'rgba(239,68,68,0.08)',
  };
  const fg: Record<typeof color, string> = {
    primary: '#7C3AED',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
  };
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ '&:last-child': { pb: 2 }, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: bg[color], color: fg[color], width: 36, height: 36 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const current = options.find((o) => o.value === value);
  const isDefault = value === 'all';
  return (
    <Box ref={anchorRef} sx={{ display: 'inline-flex' }}>
      <Chip
        size="small"
        label={`${label}: ${current?.label ?? value}`}
        variant={isDefault ? 'outlined' : 'filled'}
        color={isDefault ? 'default' : 'primary'}
        onClick={() => setOpen((p) => !p)}
        {...(!isDefault
          ? { onDelete: () => onChange('all'), deleteIcon: <ArrowDropDownRounded /> }
          : {})}
      />
      <Popper open={open} anchorEl={anchorRef.current} transition placement="bottom-start">
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={4} sx={{ mt: 1, minWidth: 200 }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList dense>
                  {options.map((o) => (
                    <MenuItem
                      key={o.value}
                      selected={o.value === value}
                      onClick={() => {
                        onChange(o.value);
                        setOpen(false);
                      }}
                    >
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

function statusVisuals(s: TemplateStatus): {
  labelKey: string;
  color: 'success' | 'warning' | 'default';
} {
  switch (s) {
    case 'published':
      return { labelKey: 'courseTemplates.status.published', color: 'success' };
    case 'draft':
      return { labelKey: 'courseTemplates.status.draft', color: 'warning' };
    case 'archived':
      return { labelKey: 'courseTemplates.status.archived', color: 'default' };
  }
}

function TemplateCard({
  template,
  program,
  onEdit,
  onDelete,
  onDuplicate,
  onCcd,
  duplicating,
}: {
  template: TemplateRow;
  program: ProgramSummary | undefined;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onCcd: () => void;
  duplicating: boolean;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { t: tCap } = useTranslation('cap');
  const color = programColor(program, template.id);
  const incomplete = isIncomplete(template);
  const status = statusVisuals(template.status);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderLeft: `4px solid ${color}`,
        transition: 'box-shadow 0.18s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Avatar sx={{ bgcolor: color, color: 'white', width: 44, height: 44, fontWeight: 700 }}>
        {program?.code?.slice(0, 2).toUpperCase() ??
          template.code?.slice(0, 2).toUpperCase() ??
          '—'}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {template.code && (
            <Chip
              label={template.code}
              size="small"
              sx={{ fontFamily: 'monospace', fontSize: 11, height: 22 }}
            />
          )}
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {template.title}
          </Typography>
          <Chip
            label={t(status.labelKey)}
            size="small"
            color={status.color}
            sx={{ height: 22, fontSize: 11 }}
          />
          <Chip
            label={`v${template.version}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: 11 }}
          />
          {template.source === 'pdf-import' && (
            <Tooltip
              title={
                template.importedFrom
                  ? t('courseTemplates.card.importedFrom', { source: template.importedFrom })
                  : t('courseTemplates.card.importedFromPdf')
              }
            >
              <Chip
                icon={<UploadFileRounded sx={{ fontSize: 12 }} />}
                label="PDF"
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: 11 }}
              />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {t('courseTemplates.card.moduleCount', { count: template.moduleCount })} ·{' '}
          {t('courseTemplates.card.assignmentCount', { count: template.assignmentCount })}
          {template.updatedAt &&
            ` · ${t('courseTemplates.card.updated', {
              date: formatDate(template.updatedAt),
            })}`}
        </Typography>
        {incomplete && (
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
            <WarningAmberRounded sx={{ fontSize: 14, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.main">
              {t('courseTemplates.card.incomplete')}
            </Typography>
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={0.5}>
        <Tooltip title={t('courseTemplates.card.edit')}>
          <IconButton onClick={onEdit}>
            <EditNoteRounded />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('courseTemplates.card.duplicate')}>
          <span>
            <IconButton onClick={onDuplicate} disabled={duplicating}>
              {duplicating ? <CircularProgress size={18} /> : <ContentCopyRounded />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={template.courseId ? tCap('ccd.openEditor') : tCap('ccd.errors.templateNotLinked')}
        >
          {/* A disabled MUI button fires no events, so the tooltip needs the
              span — which is exactly the case that has something to explain. */}
          <span>
            <IconButton onClick={onCcd} disabled={!template.courseId}>
              <WorkspacePremiumRounded />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('courseTemplates.card.delete')}>
          <IconButton color="error" onClick={onDelete}>
            <DeleteOutlineRounded />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

// ─── New template dialog (unchanged from original behavior) ─────────────────

function NewTemplateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      setCode('');
      setErr(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (!title.trim()) {
      setErr(t('courseTemplates.newDialog.titleRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const { id } = await courseTemplateApi.createTemplate({
        tenantId: currentTenantId,
        title: title.trim(),
        ...(code.trim() ? { code: code.trim() } : {}),
      });
      onCreated(id);
    } catch (e) {
      setErr(errorText(e, t('courseTemplates.errorGeneric')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('courseTemplates.newDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            label={t('courseTemplates.newDialog.titleLabel')}
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('courseTemplates.newDialog.titlePlaceholder')}
          />
          <TextField
            label={t('courseTemplates.newDialog.codeLabel')}
            size="small"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('courseTemplates.newDialog.codePlaceholder')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('courseTemplates.newDialog.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? t('courseTemplates.newDialog.creating')
            : t('courseTemplates.newDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
